/* eslint-disable complexity */

import React, { useCallback, useRef } from 'react';

import Box from 'carbon-react/lib/components/box/box.component';
import Typography from 'carbon-react/lib/components/typography/typography.component';

import { useIntl } from '@futrli/localisation';
import { WizardFormErrors } from '@futrli/ui-components/components/sidebar/wizard/types';

import { DayOfMonthMenu } from '../menus/day-of-month-menu/day-of-month-menu';
import { FormulaActions } from './formula-actions/formula-actions';
import { FormulaEditorLexical, FormulaEditorLexicalHandle } from './formula-editor-lexical/formula-editor';
import { FormulaReferenceOption } from '../../../utils/formulas/types';
import { localizeFormula } from '../../../utils/formulas/i18n-utils';
import { MonthsOfYearMenu } from '../menus/months-of-year-menu/months-of-year-menu';
import { OutputFrequency } from '../types';
import { OutputFrequencyMenu } from '../menus/output-frequency-menu/output-frequency-menu';

import styles from './formula-content-form.scss';

interface FormulaContentFormLexicalProps {
  autoFocus?: boolean;
  errors?: WizardFormErrors;
  formulaContent?: string;
  initialEditorOptions?: FormulaReferenceOption[];
  isDisabled?: boolean;
  onFormulaChange?: (formula: string) => void;
  onOutputFrequencyChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onOutputOnDayChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onOutputOnMonthChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  outputFrequency?: OutputFrequency;
  outputOnDay?: string;
  outputOnMonth?: string;
  showFieldErrors?: boolean;
}

/**
 * Renders the formula editor and output controls used by the driver builder wizard.
 */
export const FormulaContentFormLexical = ({
  autoFocus = true,
  errors,
  formulaContent,
  initialEditorOptions,
  isDisabled = false,
  onFormulaChange,
  onOutputFrequencyChange,
  onOutputOnDayChange,
  onOutputOnMonthChange,
  outputFrequency,
  outputOnDay,
  outputOnMonth,
  showFieldErrors = false
}: FormulaContentFormLexicalProps) => {
  const intl = useIntl();
  const editorRef = useRef<FormulaEditorLexicalHandle>(null);

  const handleActionClick = useCallback((action: string) => {
    editorRef.current?.insertTextAtCursor(localizeFormula(action));
  }, []);

  return (
    <div>
      <div className={styles.menuBar} data-testid="menuBar" style={{ marginBottom: 24 }}>
        <OutputFrequencyMenu
          className={styles.formInput}
          onChange={onOutputFrequencyChange}
          value={outputFrequency}
          error={!!errors?.outputFrequency || (showFieldErrors && !outputFrequency)}
          disabled={isDisabled}
          size="medium"
        />
        {(outputFrequency === OutputFrequency.monthly || outputFrequency === OutputFrequency.annually) && (
          <DayOfMonthMenu
            className={styles.formInput}
            onChange={onOutputOnDayChange}
            value={outputOnDay}
            error={!!errors?.outputOnDay || (showFieldErrors && !outputOnDay)}
            disabled={isDisabled}
            size="medium"
          />
        )}
        {outputFrequency === OutputFrequency.annually && (
          <MonthsOfYearMenu
            className={styles.formInput}
            onChange={onOutputOnMonthChange}
            value={outputOnMonth}
            error={!!errors?.outputOnMonth || (showFieldErrors && !outputOnMonth)}
            disabled={isDisabled}
            size="medium"
          />
        )}
      </div>
      <Box mb={1}>
        <Typography variant="strong" mb={0} className={styles.formulaLabel}>
          {intl.formatMessage({
            id: 'common.formula',
            defaultMessage: 'Formula'
          })}
        </Typography>
        <Typography color="var(--colorsUtilityYin055)" mb={0}>
          {intl.formatMessage({
            id: 'predict.type_for_driver_references',
            defaultMessage: 'Type @ for non financial driver references.'
          })}
        </Typography>
      </Box>
      <Box mb={3}>
        <FormulaEditorLexical
          ref={editorRef}
          autoFocus={autoFocus}
          initialFormulaContent={formulaContent}
          initialEditorOptions={initialEditorOptions}
          newStyling
          onFormulaChange={onFormulaChange}
          readOnly={isDisabled}
        />
      </Box>
      <FormulaActions onActionClick={handleActionClick} outputFrequency={outputFrequency} newStyling />
    </div>
  );
};
